export type DistinctServiceContext =
  | 'separate_encounter'
  | 'separate_structure'
  | 'separate_practitioner'
  | 'unusual_non_overlapping';

export const LEGACY_DISTINCT_PROCEDURAL_MODIFIER = '59';
export const DEFAULT_DISTINCT_SERVICE_CONTEXT: DistinctServiceContext = 'separate_structure';
export const DEFAULT_DISTINCT_SERVICE_MODIFIER = 'XS';
export const XEPSU_MODIFIERS = ['XE', 'XS', 'XP', 'XU'] as const;

export type XepsuModifier = typeof XEPSU_MODIFIERS[number];

export const DISTINCT_SERVICE_CONTEXT_TO_MODIFIER: Record<DistinctServiceContext, XepsuModifier> = {
  separate_encounter: 'XE',
  separate_structure: 'XS',
  separate_practitioner: 'XP',
  unusual_non_overlapping: 'XU',
};

export function normalizeModifierCode(modifier: string): string {
  return modifier.trim().replace(/^-/, '').toUpperCase();
}

export function isXepsuModifier(modifier: string): modifier is XepsuModifier {
  return (XEPSU_MODIFIERS as readonly string[]).includes(normalizeModifierCode(modifier));
}

export function getDistinctServiceModifier(
  context: DistinctServiceContext = DEFAULT_DISTINCT_SERVICE_CONTEXT,
): XepsuModifier {
  return DISTINCT_SERVICE_CONTEXT_TO_MODIFIER[context];
}

export function normalizeDistinctServiceModifiers(
  modifiers: string[] | null | undefined,
  context: DistinctServiceContext = DEFAULT_DISTINCT_SERVICE_CONTEXT,
): string[] {
  const normalized = uniqueNormalized(modifiers || []);
  const replacement = getDistinctServiceModifier(context);
  const hasLegacy59 = normalized.includes(LEGACY_DISTINCT_PROCEDURAL_MODIFIER);
  const hasSpecificXepsu = normalized.some(isXepsuModifier);

  if (hasSpecificXepsu) {
    return normalized.filter(modifier => modifier !== LEGACY_DISTINCT_PROCEDURAL_MODIFIER);
  }

  if (hasLegacy59) {
    return normalized.map(modifier =>
      modifier === LEGACY_DISTINCT_PROCEDURAL_MODIFIER ? replacement : modifier,
    );
  }

  return normalized;
}

export function getSecondaryDefaultModifiers(
  modifiers: string[] | null | undefined = null,
  context: DistinctServiceContext = DEFAULT_DISTINCT_SERVICE_CONTEXT,
): string[] {
  const normalized = normalizeDistinctServiceModifiers(modifiers, context);
  const hasDistinctServiceModifier = normalized.some(modifier =>
    modifier === LEGACY_DISTINCT_PROCEDURAL_MODIFIER || isXepsuModifier(modifier),
  );

  if (hasDistinctServiceModifier) {
    return normalized;
  }

  return [...normalized, getDistinctServiceModifier(context)];
}

function uniqueNormalized(modifiers: string[]): string[] {
  return Array.from(new Set(modifiers.map(normalizeModifierCode).filter(Boolean)));
}
